/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";
import config1 from "../config/profileCard.config.json"
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_USER_LIST } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let whereCls: any = [];
    let filter: any = [];
    whereCls = [];
    if (this.getFilter(args?.criteria) !== null) {
      whereCls.push(this.getFilter(args?.criteria));
    }
    try {
      if (client) {
        const res = await client.query({
          query: GET_USER_LIST,
          variables: {
            input: {
              where: whereCls,
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              invitedByMe: false,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _totalPages = res?.data?.getUserList?.totalCount
            ? parseInt(res?.data?.getUserList?.totalCount) / 10
            : 1;

          let _currentPage =
            res?.data?.getUserList?.currentPage /
            res?.data?.getUserList?.pageSize +
            1;
          let _rowData = this.formatUserTable(
            res?.data?.getUserList?.data,
            res?.data?.getUserList?.data
          );
          return {
            ...config,
            response: res?.data?.getUserList?.data,
            ultUsers: _rowData,
            userId: res?.data?.getUserList?.data[0]?.vuid,
            profileDetails: this.getFormattedProfile(
              res?.data?.getUserList?.data
            ),
            tweetyProps: this.formatFormProps(
              config1.TWEETY_PROPS,
              res?.data?.getUserList?.data,
            ),
            roles: res?.data?.getUserList?.data[0]?.role,
            isEmpty: res?.data?.getUserList?.data?.length <= 0,
            activePage: _currentPage,
            pageSize: res?.data?.getUserList?.pageSize,
            totalCount: res?.data?.getUserList?.totalCount,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      return {
        isEmpty: true,
      };
    }
  }



  private formatUserTable(data: any, roles: any[]) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          id: item?.id,
          vuid: item?.vuid,
          status: this.getFormattedStatus(item?.accountStatus, item?.suspended),
          name: `${item?.firstName} ${item?.lastName}`,
          email: item?.email,
          group: item?.group,
          image: item?.image,
          gender: item?.gender ? item?.gender : "-",
          invitedBy: item?.creatorUser?.fullName
            ? item?.creatorUser?.fullName
            : "-",
          verified: item?.emailVerified,
          role: this._getRole(item?.role),
        };
      });
    return _rowData;
  }

  private getFormattedProfile(data: any) {
    const index = data.length - 1;
    let title = "Suspend";
    let key = "suspend";
    let color = "danger";
    let isPresent = true;
    console.log("data update", pretty(data))
    if (data[index].suspended === true) {
      title = "Activate";
      key = "activate";
      color = "success";
      isPresent = true;
    }
    let _profileInfo: any = {
      title: null,
      subtitle: null,
      image: data[index]?.image ? data[index]?.image : "",
      description: data[index]?.bio,
      listItems: this.updateSubTitles(config?.listItems, data[index]),
      initialData: {
        id: data[index]?.id,
        firstName: data[index]?.firstName,
        lastName: data[index]?.lastName,
        avatar: data[index]?.image,
        phoneNumber: data[index]?.primaryContactNo,
        gender: data[index]?.gender,
        dob: data[index]?.dob,
        address1: data[index]?.address?.addressLine1,
        address2: data[index]?.address?.addressLine2,
        address3: data[index]?.address?.addressLine3,
        city: data[index]?.address?.city,
        state: data[index]?.address?.state,
        country: data[index]?.address?.country,
        zipcode: data[index]?.address?.zipcode,
      },
      buttons: [
        {
          key: key,
          title: title,
          color: color,
          isPresent: isPresent
        },
        {
          key: "update",
          title: "Update Profile",
          color: "primary",
          isPresent: true
        },
      ],
    };
    return _profileInfo;
  }
  private formatFormProps(formProps: any, data: any) {
    data = data[data.length - 1];
    let _res: any = formProps;
    _res.initialData = {
      firstName: data?.firstName,
      lastName: data?.lastName,
      phoneNumber: data?.primaryContactNo,
      avatar: data?.image,
      gender: data?.gender,
      dob: data?.dob,
      address1: data?.address?.addressLine1,
      address2: data?.address?.addressLine2,
      address3: data?.address?.addressLine3,
      city: data?.address?.city,
      state: data?.address?.state,
      country: data?.address?.country,
      zipcode: data?.address?.zipcode
    };
    return _res;
  }
  private updateSubTitles(listItems: any, data: any) {
    return listItems
      .map((item: any) => {
        const key = item.key;
        const value = this.getItemValue(key, data);
        return { ...item, subTitle: value };
      })
      .filter(
        (item: any) =>
          item.subTitle !== null &&
          item.subTitle !== "" &&
          item.subTitle !== undefined
      );
  }
  private getItemValue(key: any, data: any) {
    if (data) {
      switch (key) {
        case "email":
          return data.email;
        case "firstName":
          return data.firstName;
        case "lastName":
          return data.lastName;
        case "nationality":
          return data.nationality;
        case "image":
          return data.image;
        case "phoneNumber":
          return data?.primaryContactNo;
        case "dob":
          return data.dob;
        case "gender":
          return data.gender;
        case "address1":
          return data?.address?.addressLine1;
        case "address2":
          return data?.address?.addressLine2;
        case "address3":
          return data?.address?.addressLine3;
        case "city":
          return data?.address?.city;
        case "state":
          return data?.address?.state;
        case "country":
          return data?.address?.country;
        case "zipcode":
          return data?.address?.zipcode;
        case "Boxes":
          return data?.Boxes;
        case "nationality":
          return data?.nationality;
        default:
          return "";
      }
    }
    return "";
  }

  private _getRole(roles: any[]) {
    let _res: any[] = [];
    roles &&
      Array.isArray(roles) &&
      roles.length > 0 &&
      roles.map((item: any, index: number) => {
        _res.push(item?.name);
      });
    return _res;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LLL");
    }
  }

  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "suspended";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "created";
        case "USER_INVITED":
          return "invited";
        case "USER_VERIFIED":
          return "verified";
        case "USER_ACC_CREATED":
          return "active";
        case "SUSPENDED":
          return "suspended";
        default:
          return "-";
      }
    }
  }

  private getFilter(criteria: any) {
    if (criteria?.firstName) {
      return {
        column: "firstName",
        op: "Contains",
        value: criteria?.firstName.toLowerCase(),
      };
    }
    if (criteria?.email) {
      return {
        column: "email",
        op: "Contains",
        value: criteria?.email.toLowerCase(),
      };
    }
    return null;
  }
}
